import { useRef, useState, type ReactNode } from 'react';
import './UploadZone.css';

interface Props {
  accept?: string;
  onFile: (file: File) => void;
  /** 0..1 progress while uploading. Presence of any number = uploading. */
  progress?: number | null;
  /** Truthy = done. Pairs with `filename` for the done-state label. */
  done?: boolean;
  filename?: string | null;
  label?: string;
  hint?: string;
  /** Optional preview content for the idle state (e.g. an image). */
  preview?: ReactNode;
  /** Allow replacing the file after done/upload (defaults true). */
  allowReplace?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  /** Apply an aspect ratio (e.g. 16/9) to the idle zone. Default: none. */
  aspectRatio?: number | string;
  /** Accept multiple files (defaults false). */
  multiple?: boolean;
  onFiles?: (files: File[]) => void;
  /** Visual shape — "circle" rounds preview + zone edges for avatars. */
  shape?: 'rectangle' | 'circle';
  /** Cap the rendered width (avatars shouldn't stretch full column). */
  maxWidth?: number | string;
}

/**
 * Reusable drag-and-drop upload zone.
 *
 * States: idle / drag-over / uploading / done.
 * Keyboard-accessible: Enter or Space opens the native file picker.
 */
export function UploadZone({
  accept,
  onFile,
  progress,
  done,
  filename,
  label = 'Drag & drop a file here',
  hint = 'or click to choose a file',
  preview,
  allowReplace = true,
  onClear,
  disabled,
  aspectRatio,
  multiple,
  onFiles,
  shape = 'rectangle',
  maxWidth,
}: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const uploading = typeof progress === 'number' && progress < 1 && !done;
  const busy = Boolean(uploading);

  function openPicker() {
    if (disabled || busy) return;
    input.current?.click();
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (multiple && onFiles) {
      onFiles(Array.from(files));
    } else {
      onFile(files[0]);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDrag(false);
    if (disabled || busy) return;
    handleFiles(e.dataTransfer.files);
  }

  function onKey(e: React.KeyboardEvent) {
    if (disabled || busy) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  }

  const pct = typeof progress === 'number' ? Math.round(progress * 100) : 0;

  return (
    <div
      className={[
        'uz',
        drag ? 'uz--drag' : '',
        busy ? 'uz--busy' : '',
        done ? 'uz--done' : '',
        disabled ? 'uz--disabled' : '',
        shape === 'circle' ? 'uz--circle' : '',
      ].filter(Boolean).join(' ')}
      onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={openPicker}
      onKeyDown={onKey}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || busy}
      aria-label={label}
      style={{
        ...(aspectRatio !== undefined ? { aspectRatio: String(aspectRatio) } : {}),
        ...(maxWidth !== undefined ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : {}),
      }}
    >
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => { handleFiles(e.target.files); e.currentTarget.value = ''; }}
        className="uz__input"
        tabIndex={-1}
      />

      {busy ? (
        <div className="uz__state">
          <p className="uz__headline">Uploading… {pct}%</p>
          <div className="uz__progress" aria-hidden="true">
            <div className="uz__progress-bar" style={{ width: `${pct}%` }} />
          </div>
          {filename && <p className="uz__sub">{filename}</p>}
        </div>
      ) : done ? (
        <div className="uz__state">
          <p className="uz__headline">
            <span className="uz__check" aria-hidden="true">✓</span> Uploaded
          </p>
          {filename && <p className="uz__sub">{filename}</p>}
          {allowReplace && (
            <div className="uz__actions">
              <button
                type="button"
                className="uz__btn"
                onClick={(e) => { e.stopPropagation(); openPicker(); }}
                disabled={disabled}
              >
                Replace
              </button>
              {onClear && (
                <button
                  type="button"
                  className="uz__btn uz__btn--ghost"
                  onClick={(e) => { e.stopPropagation(); onClear(); }}
                  disabled={disabled}
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="uz__state">
          {preview && <div className="uz__preview">{preview}</div>}
          <p className="uz__headline">{label}</p>
          <p className="uz__sub">{hint}</p>
          <div className="uz__actions">
            <button
              type="button"
              className="uz__btn"
              onClick={(e) => { e.stopPropagation(); openPicker(); }}
              disabled={disabled}
            >
              Choose file
            </button>
            {onClear && preview && (
              <button
                type="button"
                className="uz__btn uz__btn--ghost"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                disabled={disabled}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
